import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InsightsIcon from '@mui/icons-material/Insights';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LanguageIcon from '@mui/icons-material/Language';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BoltIcon from '@mui/icons-material/Bolt';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SectionWrapper from '../components/shared/SectionWrapper';
import GradientText from '../components/shared/GradientText';
import AnimatedCard from '../components/shared/AnimatedCard';

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const pillars = [
  {
    icon: LinkIcon,
    title: 'Connect your sources',
    description:
      'Plug in Google Analytics, Search Console, your CMS, and social platforms in a few clicks. ZerveAnalytics pulls everything into one place — no scripts, no spreadsheets.',
  },
  {
    icon: DashboardIcon,
    title: 'One dashboard, every KPI',
    description:
      'See traffic, rankings, conversions, and AI visibility over time on a single overview. Slice by page, channel, query, or model — without building a single chart.',
  },
  {
    icon: TipsAndUpdatesIcon,
    title: 'Recommendations that ship',
    description:
      'Get specific, prioritized recommendations to improve each page — title fixes, content gaps, schema additions, internal links — ranked by potential impact.',
  },
  {
    icon: TrendingUpIcon,
    title: 'Track what you improved',
    description:
      'Every change is tracked. Watch rankings, impressions, and AI mentions move week-over-week so you know exactly what is working.',
  },
];

const trackedMetrics = [
  {
    icon: SearchIcon,
    label: 'Traditional SEO',
    points: [
      'Organic rankings per keyword and per page',
      'Impressions, clicks, and CTR from Search Console',
      'Core Web Vitals and indexability',
      'Backlinks and referring domains',
    ],
  },
  {
    icon: SmartToyIcon,
    label: 'LLM visibility',
    points: [
      'When your brand appears in ChatGPT, Claude, Gemini, and Perplexity',
      'Which queries surface your content as a source',
      'Citation share vs. your competitors',
      'How your positioning shifts model-to-model',
    ],
  },
  {
    icon: LanguageIcon,
    label: 'Site & content health',
    points: [
      'Page-by-page engagement and conversion',
      'Topic coverage gaps vs. ranking competitors',
      'Schema, structured data, and metadata audits',
      'Internal linking opportunities',
    ],
  },
];

const howItWorks = [
  {
    step: '01',
    title: 'Connect your stack',
    description:
      'Authorize the analytics and content sources you already use. ZerveAnalytics starts a per-customer pipeline immediately — no SQL, no warehouse setup.',
    icon: LinkIcon,
  },
  {
    step: '02',
    title: 'See your baseline',
    description:
      'Within minutes you get a baseline dashboard: where you rank, where you appear in AI answers, which pages convert, and where you are losing ground.',
    icon: InsightsIcon,
  },
  {
    step: '03',
    title: 'Get ranked recommendations',
    description:
      'Each page gets a prioritized to-do list — quick wins on top, longer plays below — each tied to the KPI it is expected to move.',
    icon: TipsAndUpdatesIcon,
  },
  {
    step: '04',
    title: 'Measure the lift',
    description:
      'As you ship changes, ZerveAnalytics attributes the movement back to the recommendation. You see what worked, what did not, and what to try next.',
    icon: TrendingUpIcon,
  },
];

const pricingTiers = [
  {
    icon: CardGiftcardIcon,
    name: 'Free trial',
    price: '$0',
    period: 'for one month',
    desc: 'Full access to the platform on one site for 30 days. No credit card required to start.',
    points: [
      'One site / project',
      'All source integrations',
      'Full recommendations engine',
      'AI visibility tracking included',
    ],
    cta: 'Join the waitlist',
  },
  {
    icon: WorkspacePremiumIcon,
    name: 'Individual',
    price: 'Per site',
    period: 'flat monthly fee',
    desc: 'Affordable, predictable pricing — one flat monthly fee per site or project. Built for solo operators, indie founders, and consultants who want real analytics without enterprise pricing.',
    points: [
      'One site / project per subscription',
      'Add more sites as your portfolio grows',
      'Weekly recommendations refresh',
      'Email + dashboard alerts on KPI shifts',
    ],
    cta: 'Get notified at launch',
    featured: true,
  },
];

const fade = {
  initial: { opacity: 0, y: 20 } as const,
  whileInView: { opacity: 1, y: 0 } as const,
  viewport: { once: true, margin: '-40px' as const },
  transition: { duration: 0.6, ease: 'easeOut' as const },
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const ZerveAnalyticsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const mailtoWaitlist =
    'mailto:kevin@zerveme.com?subject=ZerveAnalytics%20waitlist&body=I%27d%20like%20early%20access%20to%20ZerveAnalytics.';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      <Box component="main" sx={{ flex: 1 }}>
        {/* ===== HERO ===== */}
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#0c0c0c',
          }}
        >
          <Container maxWidth="md" sx={{ textAlign: 'center', py: 16 }}>
            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }}>
              <Chip
                label="COMING SOON"
                size="small"
                sx={{
                  mb: 4,
                  fontWeight: 700,
                  letterSpacing: 2,
                  fontSize: '0.7rem',
                  bgcolor: 'rgba(37, 99, 235, 0.12)',
                  color: 'primary.main',
                  border: '1px solid rgba(37, 99, 235, 0.25)',
                }}
              />
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3rem', sm: '4rem', md: '5.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.0,
                  mb: 3,
                  color: '#ffffff',
                }}
              >
                ZerveAnalytics
              </Typography>
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.35 }}>
              <Typography
                variant="h4"
                sx={{
                  maxWidth: 640,
                  mx: 'auto',
                  mb: 3,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  color: 'primary.main',
                  fontFamily: '"Bricolage Grotesque", sans-serif',
                }}
              >
                Improve your online presence — across search and AI.
              </Typography>
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.5 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 560, mx: 'auto', mb: 6, lineHeight: 1.8 }}
              >
                Connect your sources, see every KPI that matters in one dashboard,
                and get specific recommendations to climb in Google — and start showing
                up in ChatGPT, Claude, Gemini, and Perplexity. Affordable, flat-rate
                pricing per site.
              </Typography>
            </motion.div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.65 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<NotificationsActiveIcon />}
                  href={mailtoWaitlist}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '0.9rem',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: '#1d4ed8' },
                  }}
                >
                  Join the Waitlist
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    const el = document.querySelector('#what-it-does');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '0.9rem',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    '&:hover': { borderColor: '#ffffff', bgcolor: 'transparent' },
                  }}
                >
                  See What&rsquo;s Coming
                </Button>
              </Stack>
            </motion.div>
          </Container>
        </Box>

        {/* ===== WHAT IT DOES ===== */}
        <SectionWrapper id="what-it-does">
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 3, fontSize: '0.7rem', mb: 3, display: 'block' }}
              >
                WHAT ZERVEANALYTICS DOES
              </Typography>
              <GradientText variant="h2" sx={{ mb: 3, maxWidth: 560 }}>
                Your whole online presence in one place
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
                Stop stitching together six tools, three spreadsheets, and a hunch.
                ZerveAnalytics gives you one dashboard, one source of truth, and a
                clear list of changes to make next.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {pillars.map((p, i) => (
              <Grid size={{ xs: 12, sm: 6 }} key={p.title}>
                <AnimatedCard delay={i * 0.08} sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      mb: 2.5,
                    }}
                  >
                    <p.icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
                    {p.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {p.description}
                  </Typography>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== KPIs WE TRACK ===== */}
        <SectionWrapper id="kpis" dark>
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 3, fontSize: '0.7rem', mb: 3, display: 'block' }}
              >
                BUILT FOR THE NEW SEARCH LANDSCAPE
              </Typography>
              <GradientText variant="h2" sx={{ mb: 3, maxWidth: 600 }}>
                Track what Google sees — and what the LLMs see
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
                Search no longer ends at the blue-link results page. ZerveAnalytics
                tracks both traditional rankings and your visibility inside the
                AI assistants people actually use to make decisions.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {trackedMetrics.map((m, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={m.label}>
                <AnimatedCard delay={i * 0.08} sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      mb: 2.5,
                    }}
                  >
                    <m.icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#ffffff' }}>
                    {m.label}
                  </Typography>
                  <List disablePadding dense>
                    {m.points.map((pt) => (
                      <ListItem key={pt} disableGutters sx={{ py: 0.4, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={pt}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                            sx: { lineHeight: 1.6 },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== HOW IT WORKS ===== */}
        <SectionWrapper id="how-it-works">
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <GradientText variant="h2" sx={{ mb: 3 }}>
                How it works
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                From sign-up to your first measurable lift — four steps,
                no SQL, no data team required.
              </Typography>
            </Box>
          </motion.div>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {howItWorks.map((step) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5 }}
              >
                <Card sx={{ p: { xs: 3, md: 5 } }}>
                  <Grid container spacing={4} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Bricolage Grotesque", sans-serif',
                          fontSize: '2rem',
                          fontWeight: 800,
                          color: 'rgba(255, 255, 255, 0.08)',
                          lineHeight: 1,
                        }}
                      >
                        {step.step}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 11 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(255, 255, 255, 0.04)',
                            border: '1px solid rgba(255, 255, 255, 0.06)',
                            flexShrink: 0,
                          }}
                        >
                          <step.icon sx={{ fontSize: 20, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, maxWidth: 720 }}>
                        {step.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              </motion.div>
            ))}
          </Box>
        </SectionWrapper>

        {/* ===== PRICING ===== */}
        <SectionWrapper id="pricing" dark>
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 3, fontSize: '0.7rem', mb: 3, display: 'block' }}
              >
                AFFORDABLE — PRICED PER SITE
              </Typography>
              <GradientText variant="h2" sx={{ mb: 3, maxWidth: 560 }}>
                Start free. Then pay one flat fee per site.
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
                No seat counts. No usage tiers. No surprise invoices.
                One affordable monthly price per site or project — add more only when you grow.
                Team and agency tiers are on the roadmap.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5} justifyContent="center">
            {pricingTiers.map((tier, i) => (
              <Grid size={{ xs: 12, md: 5 }} key={tier.name}>
                <AnimatedCard
                  delay={i * 0.1}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    border: tier.featured
                      ? '1px solid rgba(37, 99, 235, 0.4)'
                      : '1px solid rgba(255, 255, 255, 0.06)',
                    bgcolor: tier.featured ? 'rgba(37, 99, 235, 0.04)' : undefined,
                  }}
                >
                  {tier.featured && (
                    <Chip
                      label="LAUNCH PLAN"
                      size="small"
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        letterSpacing: 1,
                        fontSize: '0.65rem',
                        bgcolor: 'rgba(37, 99, 235, 0.15)',
                        color: 'primary.main',
                        border: '1px solid rgba(37, 99, 235, 0.3)',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      mb: 2.5,
                    }}
                  >
                    <tier.icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#ffffff' }}>
                    {tier.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Bricolage Grotesque", sans-serif',
                        fontWeight: 800,
                        fontSize: '2rem',
                        color: '#ffffff',
                        lineHeight: 1,
                      }}
                    >
                      {tier.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tier.period}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                    {tier.desc}
                  </Typography>
                  <List disablePadding dense sx={{ mb: 3 }}>
                    {tier.points.map((pt) => (
                      <ListItem key={pt} disableGutters sx={{ py: 0.4, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={pt}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                            sx: { lineHeight: 1.6 },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    fullWidth
                    variant={tier.featured ? 'contained' : 'outlined'}
                    href={mailtoWaitlist}
                    sx={{
                      py: 1.3,
                      fontSize: '0.85rem',
                      ...(tier.featured
                        ? {
                            bgcolor: 'primary.main',
                            '&:hover': { bgcolor: '#1d4ed8' },
                          }
                        : {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            color: '#ffffff',
                            '&:hover': { borderColor: '#ffffff', bgcolor: 'transparent' },
                          }),
                    }}
                  >
                    {tier.cta}
                  </Button>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== UNDER THE HOOD ===== */}
        <SectionWrapper id="pipeline">
          <motion.div {...fade}>
            <Box sx={{ mb: 10 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 3, fontSize: '0.7rem', mb: 3, display: 'block' }}
              >
                UNDER THE HOOD
              </Typography>
              <GradientText variant="h2" sx={{ mb: 3, maxWidth: 560 }}>
                A lightweight pipeline per customer
              </GradientText>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
                Each subscription gets its own dedicated, isolated pipeline. Built lean
                — no Databricks-scale overhead, no shared multi-tenant warehouse, no
                noisy-neighbor surprises in your dashboards.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {[
              {
                icon: AccountTreeIcon,
                title: 'Isolated by design',
                desc: 'Your data lives in your pipeline. No comingling, no shared compute, no awkward conversations about who saw what.',
              },
              {
                icon: BoltIcon,
                title: 'Lean compute',
                desc: 'Built on cost-efficient modern data tools — not heavyweight enterprise warehouses. Pricing stays approachable for solo operators.',
              },
              {
                icon: LockIcon,
                title: 'Yours to leave with',
                desc: 'Cancel anytime and export your full history. We do not hold your data hostage.',
              },
            ].map((item, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={item.title}>
                <AnimatedCard delay={i * 0.08} sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      mb: 2.5,
                    }}
                  >
                    <item.icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {item.desc}
                  </Typography>
                </AnimatedCard>
              </Grid>
            ))}
          </Grid>
        </SectionWrapper>

        {/* ===== CTA ===== */}
        <SectionWrapper
          id="zerveanalytics-contact"
          sx={{ py: { xs: 12, md: 20 }, bgcolor: '#0c0c0c' }}
        >
          <motion.div {...fade}>
            <Box sx={{ textAlign: 'center' }}>
              <Chip
                label="EARLY ACCESS"
                size="small"
                sx={{
                  mb: 4,
                  fontWeight: 700,
                  letterSpacing: 2,
                  fontSize: '0.7rem',
                  bgcolor: 'rgba(37, 99, 235, 0.12)',
                  color: 'primary.main',
                  border: '1px solid rgba(37, 99, 235, 0.25)',
                }}
              />
              <Typography
                variant="h2"
                sx={{ fontWeight: 700, mb: 3, color: '#ffffff', fontSize: { xs: '2rem', md: '3rem' } }}
              >
                Be first when ZerveAnalytics launches.
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 500, mx: 'auto', mb: 6, lineHeight: 1.8 }}
              >
                Drop your email to join the waitlist. We&rsquo;ll reach out as soon
                as we open the first round of free trials.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<EmailIcon />}
                  href={mailtoWaitlist}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '0.9rem',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: '#1d4ed8' },
                  }}
                >
                  Join the Waitlist
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LinkedInIcon />}
                  href="https://www.linkedin.com/in/kevin-singh-908547b4"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '0.9rem',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    '&:hover': { borderColor: '#ffffff', bgcolor: 'transparent' },
                  }}
                >
                  Connect on LinkedIn
                </Button>
              </Stack>

              <Button
                component={RouterLink}
                to="/"
                sx={{ mt: 5, color: 'text.secondary', fontSize: '0.85rem', '&:hover': { color: '#ffffff' } }}
              >
                Back to ZerveMe.com
              </Button>
            </Box>
          </motion.div>
        </SectionWrapper>
      </Box>

      <Footer />
    </Box>
  );
};

export default ZerveAnalyticsPage;
